import { PagingParameters } from "@/api/dtos";
import {
  useGetPortfolioItems,
  useMutatePortfolioItem,
} from "@/api/multipleprint/multipleprint";
import { useGetNotRelatedResource } from "@/api/related-fields";
import { toast } from "@/hooks/use-toast";
import { tWithKey } from "@/lib/i18n-fallback";
import { Multipleprint } from "@/lib/types";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultipleprintLinksView } from "./MultipleprintLinksView";

/**
 * Onglet Portfolio d'un multiple portfolio : les multiples simples qu'il contient
 * (table MultipleprintInPortfolio, distincte des « Enregistrements liés »).
 */
const PortfolioItemsLinks = ({ id }: { id: string }) => {
  const { t } = useTranslation();

  const { data: items } = useGetPortfolioItems(id);

  const notifyUpdated = useCallback(
    () =>
      toast({
        title: t("relatedMultipleprintUpdatedToastDescription"),
        duration: 2000,
        variant: "success",
      }),
    [t],
  );
  const { mutate: addItem } = useMutatePortfolioItem(id, "add", {
    onSuccess: notifyUpdated,
  });
  const { mutate: removeItem } = useMutatePortfolioItem(id, "remove", {
    onSuccess: notifyUpdated,
  });

  const [searchContent, setSearchContent] = useState("");
  const [listPagination, setListPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [addPagination, setAddPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  // Candidats : multiples simples pas encore dans ce portfolio (ni le portfolio lui-même).
  const { data: candidates } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: addPagination,
    searchQuery: searchContent,
    associationScope: "portfolioItems",
    multipleprintNature: "simple",
  });

  // La liste du portfolio est chargée en entier : pagination côté client.
  const paginatedItems = useMemo(() => {
    const start = listPagination.page * listPagination.pageSize;
    return (items ?? []).slice(start, start + listPagination.pageSize);
  }, [items, listPagination.page, listPagination.pageSize]);

  return (
    <MultipleprintLinksView
      storageKey="portfolio-items-links-table"
      title={tWithKey(
        t,
        "multipleprint.portfolio.items",
        "Multiples du portfolio",
      )}
      // Flèche « retour » du mode ajout vers la liste du portfolio.
      backLabel={tWithKey(
        t,
        "multipleprint.portfolio.backToItems",
        "Multiples du portfolio",
      )}
      linked={paginatedItems}
      linkedPagination={{
        pagination: listPagination,
        setPage: setListPagination,
        totalSize: items?.length ?? 0,
      }}
      candidates={candidates?.data ?? []}
      candidatesTotalSize={candidates?.totalSize ?? 0}
      candidatesPagination={addPagination}
      setCandidatesPagination={setAddPagination}
      searchContent={searchContent}
      setSearchContent={setSearchContent}
      onAdd={addItem}
      onDelete={removeItem}
    />
  );
};

export default PortfolioItemsLinks;
